import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { uncoveryApi } from "services";

const options = {
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

export const fetchNote = createAsyncThunk(
  "notes/fetchNote",
  async ({ entityId, assetId }, { rejectWithValue }) => {
    const query = `/entities/${entityId}/assets/${assetId}/note`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data.request);
      }
    } catch (e) {
      return rejectWithValue(e.response.data);
    }
  }
);

export const createOrUpdateNote = createAsyncThunk(
  "notes/createOrUpdateNote",
  async ({ entityId, assetId, content }, { rejectWithValue }) => {
    const query = `/entities/${entityId}/assets/${assetId}/note`;
    const body = { content };
    try {
      const response = await uncoveryApi.post(query, body);
      if (response.status === 201 || response.status === 200) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data);
      }
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const deleteNote = createAsyncThunk(
  "notes/deleteNote",
  async ({ entityId, assetId, noteId }, { rejectWithValue }) => {
    const query = `/entities/${entityId}/assets/${assetId}/note/${noteId}`;
    try {
      const response = await uncoveryApi.delete(query, options);
      if (response.status === 200) {
        return noteId;
      } else {
        return rejectWithValue(response.data.request);
      }
    } catch (err) {
      return rejectWithValue(err.response?.data.request);
    }
  }
);

export const fetchAllNotes = createAsyncThunk(
  "notes/fetchAllNotes",
  async ({ entityId }, { rejectWithValue }) => {
    const query = `/entities/${entityId}/notes`;
    try {
      const response = await uncoveryApi.get(query);
      if (response.status === 200) {
        return response.data.data;
      } else {
        return rejectWithValue(response.data.request);
      }
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

const initialState = {
  note: null,
  notes: [],
  fetchStatus: "idle",
  addUpdateStatus: "idle",
  deleteStatus: "idle",
  error: null,
};

const notesSlice = createSlice({
  name: "notes",
  initialState,
  reducers: {
    resetNote: (state) => {
      state.note = null;
      state.addUpdateStatus = "idle";
      state.deleteStatus = "idle";
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNote.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null; // Clear any previous error
      })
      .addCase(fetchNote.fulfilled, (state, { payload }) => {
        state.note = payload;
        state.fetchStatus = "success";
        state.error = null; // Clear error on success
      })
      .addCase(fetchNote.rejected, (state, { payload }) => {
        state.error = payload?.message || "Failed to fetch note";
        state.fetchStatus = "failed";
      })
      .addCase(createOrUpdateNote.pending, (state) => {
        state.addUpdateStatus = "loading";
        state.error = null; // Clear any previous error
      })
      .addCase(createOrUpdateNote.fulfilled, (state, { payload }) => {
        state.note = payload;
        state.addUpdateStatus = "success";
        state.error = null; // Clear error on success
      })
      .addCase(createOrUpdateNote.rejected, (state, { payload }) => {
        state.error = payload?.message || "Failed to create/update note";
        state.addUpdateStatus = "failed";
      })
      .addCase(deleteNote.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null; // Clear any previous error
      })
      .addCase(deleteNote.fulfilled, (state, { payload }) => {
        if (state.note?.id === payload) {
          state.note = null;
        }
        state.deleteStatus = "success";
        state.error = null; // Clear error on success
      })
      .addCase(deleteNote.rejected, (state, { payload }) => {
        state.error = payload?.message || "Failed to delete note";
        state.deleteStatus = "failed";
      })
      .addCase(fetchAllNotes.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null; // Clear any previous error
      })
      .addCase(fetchAllNotes.fulfilled, (state, { payload }) => {
        state.notes = payload;
        state.fetchStatus = "success";
        state.error = null;
      })
      .addCase(fetchAllNotes.rejected, (state, { payload }) => {
        state.error = payload?.message || "Failed to fetch notes";
        state.fetchStatus = "failed";
      });
  },
});

export const { resetNote, clearError } = notesSlice.actions;

export default notesSlice.reducer;
